import React, { useRef, useEffect, useState, useContext } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { animated, useSpring } from "react-spring";
import { get, isEmpty } from "lodash";
import styled, { ThemeContext } from "styled-components/macro";

import { ItemType } from "../store/models/itemType";

import { SET_SIDE } from "../store/actions/sideMenuActions";
import {
    SET_ACTIVE_MENU_PATH,
    SET_FAVORITE_OVERLAY_ACTIVE,
    SET_LAYOUT_OVERLAY_ACTIVE,
} from "../store/actions/configurationActions";
import { SET_FILTERED_MENU } from "../store/actions/filterAction";

import { SideMenu } from "../components/SideMenu/SideMenu";
import { MenusList } from "../components/Menu/MenusList";
import { ItemsList } from "../components/Item/ItemsList";
import { ConfiguratorImages } from "../components/Image/ConfiguratorImages";
import { getMenuItemPixelValue, getMenuPosition } from "../utilities/maskUtils";
import { ButtonBack } from "../components/NavigationButtons/ButtonBack";
import { ContentContainer } from "../components/SideMenu/ContentContainer";
import { SideMenuBackButton } from "../components/SideMenu/SideMenuBackButton";
import { ButtonNext } from "../components/NavigationButtons/ButtonNext";
import CustomScrollbar from "../components/CustomScrollbar/CustomScrollbar";
import { Side } from "../components/SideMenu/side";
import { ConfiguratorTotalPrice } from "../components/TotalPrice/ConfiguratorTotalPrice";
import { ShortcutButtons } from "../components/ShortcutButtons/ShortcutButtons";
import { FilterControls } from "../components/Filtering/Filter/FilterControls";
import { MenuFilter } from "../components/Filtering/Filter/MenuFilter";
import { MenuSearch } from "../components/Filtering/Search/MenuSearch";
import { MenuChips } from "../components/Filtering/Filter/MenuChips";
import { FilterContainer } from "../components/Filtering/FilteringContainer";
import { SaveImageButton } from "../components/ActionButtons/SaveImageButton";
import { SaveImageDialog } from "../components/Dialogs/SaveImageDialog";
import { SlideIn } from "../components/Animation/SlideIn";

import { getModelFromConfig } from "../utilities/configUtils";

import { useBreakpoint } from "../utilities/media-js-breakpoints";
import Constants from "../constants";

import FlipPhoneIcon from "../icons/flipPhoneIcon.png";
import {
    getFilters,
    getFilteredItems,
    getMaskMetadata,
    getMaskData,
    getMaskElement,
    getFrontendSettings,
    getShowPrice,
    getShowFilters,
    getMenuCache,
    getCurrentSummaries,
    getSelectedMenu,
    getActiveMenuPath,
    getSideMenuSide,
    getLanguage,
    getMenuDynamicType,
    getLayoutGroups,
    getConfigurationString,
    getModelId,
    getFavoriteOverlayActive,
    getLayoutOverlayActive,
    getActiveSurfaceItems,
    getSideMenuContentLoading,
    getTotalPrice,
} from "../store/sagas/selectors";
import { goToModelSelector } from "../utilities/navigationUtils";
import GaReporter, { GaEcommerceAction } from "../utilities/gaReporter";
import { VisibleItemsCounter } from "../components/SideMenu/VisibleItemsCounter";
import { FilterFooter } from "../components/SideMenu/FilterFooter";
import { MenuCategoryTabs } from "../components/Menu/MenuCategoryTabs";
import { isMenuCategory } from "../utilities/itemUtilities";
import { PatternSelector } from "../components/PatternSelector/PatternSelector";
import { ExpandMobileMenu } from "../components/MobileMenu/ExpandMobileMenu";
import { Item } from "../models/item";
import { useMenuActivityCallback } from "../utilities/utilityHooks";
import { LayoutOverlay } from "../components/Layouts/LayoutOverlay";
import { FavoritesOverlay } from "../components/Favorites/FavoritesOverlay";
import { FloatMenu } from "../components/FloatMenu/FloatMenu";
import { AppLoader } from "../components/Loader/AppLoader";

const MobileContentContainer = styled.div`
    height: 100%;
`;

const Configurator = () => {
    const { search } = useLocation();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const themeContext = useContext(ThemeContext);

    const breakpoints = useBreakpoint();
    const isMobile = breakpoints.xxs || breakpoints.xs || breakpoints.sm;
    const isHighRes = breakpoints.xxxl;

    const menuRef = useRef<any>(null);
    const scrollbarRef = useRef<any>(null);
    const scrollContainerHeightRef = useRef<number>(100);

    const frontendSettings = useSelector(getFrontendSettings);
    const showPrice = useSelector(getShowPrice);
    const showFilters = useSelector(getShowFilters);
    const maskElement = useSelector(getMaskElement);
    const maskMetadata = useSelector(getMaskMetadata);
    const maskData = useSelector(getMaskData);
    const menus = useSelector(getMenuCache);
    const summaryGroups = useSelector(getCurrentSummaries);
    const selectedMenu = useSelector(getSelectedMenu);
    const menuPath = useSelector(getActiveMenuPath);
    const language = useSelector(getLanguage);
    const filters = useSelector(getFilters);
    const filteredItems = useSelector(getFilteredItems);
    const menuDynamicType = useSelector(getMenuDynamicType);
    const layoutGroups = useSelector(getLayoutGroups);
    const configurationString = useSelector(getConfigurationString);
    const favoritesOverlayActive = useSelector(getFavoriteOverlayActive);
    const layoutOverlayActive = useSelector(getLayoutOverlayActive);
    const activeSurfaceItems = useSelector(getActiveSurfaceItems);
    const loadingMenu: boolean = useSelector(getSideMenuContentLoading);
    const totalPrice = useSelector(getTotalPrice);

    let side = useSelector(getSideMenuSide);
    const modelId = useSelector(getModelId);

    const fixedSide = Side[get(frontendSettings, "fix_side_menu") as keyof typeof Side];
    if (fixedSide !== undefined) {
        side = fixedSide;
    }

    const showFavorites = frontendSettings?.show_favorites;
    const showLayouts = frontendSettings?.show_layouts;
    const showPriceInConfigurator = frontendSettings?.show_price_in_configurator;
    const showSaveImageButton = frontendSettings?.show_save_image_button;
    const savedFavorites = localStorage.getItem("favorites");
    const currentFavorites = savedFavorites ? JSON.parse(savedFavorites) : [];
    const [showSaveImageDialog, setShowSaveImageDialog] = useState(false);

    // TODO: Try to move menuCenterY scroll handling to an item menu component
    // to avoid re-rendering everything on scroll.
    const [menuCenterY, setMenuCenterY] = useState(0);
    const [menuWidth, setMenuWidth] = useState(100);
    const [windowWidth, setWindowWidth] = useState(0);
    const [searchValue, setSearchValue] = useState("");
    const [searchFocus, setSearchFocus] = useState(false);
    const [shouldShowFilters, setShowFilters] = useState(false);
    const [mobileMenuExpanded, setMobileMenuExpanded] = useState(false);
    const [favorites, setFavorite] = useState<string[]>(currentFavorites);
    const [favoritesToRemove, setFavoritesToRemove] = useState<string[]>([]);
    const [activeItems, setActiveItems] = useState<Item[]>([]);
    const [currentMenuType, setCurrentMenuType] = useState("");

    let currentItemLength: any = 0;

    const [transitionFromRight, setTransitionFromRight] = useState(true);

    const filterContainerSpring = useSpring({
        maxHeight: shouldShowFilters ? 300 : 0,
    });

    const filterContainerSpringMobile = {
        ...useSpring({
            maxHeight: 265,
            transform: shouldShowFilters ? "translateY(-100%)" : "translateY(0%)",
            width: "100%",
            zIndex: 6,
        }),
        position: "absolute" as any,
    };

    const mobileMenuExpandedHeight = (multiplier: number) => {
        return Math.round(window.innerHeight * multiplier);
    };

    const mobileContentSpring = {
        ...useSpring({
            height: mobileMenuExpanded ? `${mobileMenuExpandedHeight(0.8)}px` : `${mobileMenuExpandedHeight(0.5)}px`,
            transform: mobileMenuExpanded ? `translateY(-${mobileMenuExpandedHeight(0.3)}px)` : "translateY(0px)",
        }),
        width: "100%",
        position: "absolute" as any,
    };

    const updateMenuCenterY = () => {
        if (scrollbarRef.current) {
            const pos = scrollbarRef.current.getValues();
            setMenuCenterY(pos.scrollTop + pos.clientHeight / 2);
        }
    };

    const updateSearchValue = (value: string) => {
        dispatch({ type: SET_FILTERED_MENU, filteredMenu: value });
        setSearchValue(value);
    };

    const onSearchFocus = (value: boolean) => {
        setSearchFocus(value);
    };

    const removeConfigurationsFromFavorites = (removedFavorites: string[]) => {
        if (removedFavorites.length > 0) {
            const tempArr = [...favorites];
            removedFavorites.forEach(favorite => {
                const index = tempArr.indexOf(favorite);
                if (index > -1) {
                    tempArr.splice(index, 1);
                }
            });
            setFavorite(tempArr);
        }
    };

    useEffect(() => {
        localStorage.setItem("favorites", JSON.stringify(favorites));
    }, [favorites]);

    useEffect(() => {
        GaReporter.trackPageView();
    }, []);

    useEffect(() => {
        if (favoritesOverlayActive) return;
        removeConfigurationsFromFavorites(favoritesToRemove);
        setFavoritesToRemove([]);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [favoritesOverlayActive]);

    useEffect(() => {
        if (scrollbarRef.current) {
            scrollbarRef.current.scrollTop(0);
            updateMenuCenterY();
        }

        setActiveItems(currentActiveSurfaces());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedMenu, menuDynamicType]);

    useEffect(() => {
        if (menuDynamicType) {
            if (currentMenuType !== menuDynamicType) {
                if (searchValue.length > 0) setSearchValue("");
                setCurrentMenuType(menuDynamicType);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [menuDynamicType]);

    useEffect(() => {
        const handleResize = () => {
            if (windowWidth !== window.innerWidth) setWindowWidth(window.innerWidth);
            if (menuRef.current) {
                setMenuWidth(menuRef.current.clientWidth);
            }
            updateMenuCenterY();
        };
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [windowWidth, isMobile]);

    useEffect(() => {
        if (menuRef.current) setMenuWidth(menuRef.current.clientWidth);
    }, [menuRef]);

    const goBackSideMenu = () => {
        GaReporter.event("menu_back", "Configurator");
        const updatedMenuPath = menuPath.filter(menu => !isMenuCategory(menu.id)).slice(0, -1);

        setShowFilters(false);
        setTransitionFromRight(false);
        dispatch({ type: SET_ACTIVE_MENU_PATH, activeMenuPath: updatedMenuPath });
    };

    const onItemSelected = (item: Item) => {
        for (let i = 0; i < activeItems.length; i += 1) {
            activeItems[i].isSelected = activeItems[i].id === item.id;
        }

        setTransitionFromRight(true);
    };

    const expandFilters = () => {
        setShowFilters(!shouldShowFilters);
        setMobileMenuExpanded(false);
    };

    const expandMobileMenu = () => {
        setMobileMenuExpanded(!mobileMenuExpanded);
        setShowFilters(false);
    };

    const toggleFavorite = (value: string | null) => {
        if (!value) return;

        let shouldReturn = false;
        favorites.forEach(favorite => {
            if (value === favorite) {
                const clone = [...favorites];
                clone.splice(favorites.indexOf(favorite), 1);
                setFavorite(clone);
                shouldReturn = true;
            }
        });

        if (shouldReturn) return;

        setFavorite([...favorites, value]);
    };

    const goToSummary = () => {
        navigate(`summary${search}`);
        const summaryItems = summaryGroups.flatMap(g => g.items);
        GaReporter.event("open_summary", "Navigation");
        GaReporter.trackProductCheckout(
            GaEcommerceAction.AddToCart,
            "Summary",
            summaryItems,
            showPrice ? totalPrice : null
        );
    };

    const shouldRenderSideMenuBackButton = () => {
        const updatedMenuPath = menuPath.filter(menu => !isMenuCategory(menu.id));
        if (updatedMenuPath.length === 0) return false;

        const parentMenuId =
            updatedMenuPath.length > 1 ? updatedMenuPath[updatedMenuPath.length - 2].id : Constants.rootMenuId;

        const parentMenu = menus[parentMenuId];
        const currentMenu = updatedMenuPath[updatedMenuPath.length - 1];

        return parentMenu?.items.some((e: Item) => e.id === currentMenu.id);
    };

    const renderSideMenuBackButton = () => {
        return shouldRenderSideMenuBackButton() ? (
            <SideMenuBackButton goBackSideMenu={goBackSideMenu} aria-hidden="true" />
        ) : null;
    };

    const filterItemsBySearch = (selecedAndFilteredMeny: any[], searchValueStr: string) => {
        const searchValueFilterFn = (element: any) => {
            return (
                (element.name && element.name.toLowerCase().indexOf(searchValueStr) > -1) ||
                (element.shortText && element.shortText.toLowerCase().indexOf(searchValueStr) > -1)
            );
        };

        return !isEmpty(selecedAndFilteredMeny)
            ? selecedAndFilteredMeny.filter((el: any) => {
                  return searchValueFilterFn(el);
              })
            : selecedAndFilteredMeny;
    };

    const currentActiveSurfaces = () => {
        const currentItems: Item[] = [];
        // Clicking menu item does not always update the menu with selected menu.
        menus[selectedMenu || Constants.rootMenuId]?.items?.forEach(item => {
            if (item.productId && activeSurfaceItems.has(item.productId)) currentItems.push(item);
        });
        return currentItems;
    };

    const renderMenuItems = () => {
        const currentMenu = menus[selectedMenu || Constants.rootMenuId];

        if (filteredItems.every((item: Item) => item.type === ItemType.Menu || item.type === ItemType.Submenu)) {
            return <MenusList key={searchValue || "menus"} onItemSelected={onItemSelected} items={filteredItems} />;
        }

        // Categories will automatically become activated and result in an item list instead.
        if (currentMenu === undefined || currentMenu?.items?.every((item: any) => item.type === ItemType.Category)) {
            return <></>;
        }

        // Special case for Ballingslov which should have large room icons.
        const largeIcons = selectedMenu === "MENU-ROOMS";
        const itemFiltered =
            searchValue.length > 0
                ? filterItemsBySearch(filteredItems, searchValue.toLocaleLowerCase(language || undefined))
                : filteredItems;

        currentItemLength = itemFiltered.length;
        const sideMenuWidth = isHighRes ? Constants.highResSideMenuWidth : Constants.sideMenuWidth;

        const ai = isMobile && searchValue.length > 0 ? [] : activeItems;

        if (!currentItemLength && ai.length === 0) return <></>;
        return (
            <ItemsList
                key={selectedMenu}
                onItemSelected={onItemSelected}
                items={itemFiltered}
                activeItems={ai}
                largeIcons={largeIcons}
                menuCenterY={menuCenterY}
                menuWidth={isMobile ? menuWidth : sideMenuWidth}
            />
        );
    };

    const handleShortcutButtonClick = (itemId: string) => {
        const pixel = getMenuItemPixelValue(itemId, maskMetadata);
        const sideMenuPosition = getMenuPosition(maskData, pixel, maskElement?.height, maskElement?.width, side);
        const menuItem = menus[Constants.rootMenuId].items.find(item => item.id === itemId);
        const breadcrumbText =
            (menuItem && (menuItem.type === ItemType.Menu ? menuItem.name : menuItem.shortText)) || "";

        setShowFilters(false);
        dispatch({ type: SET_SIDE, side: sideMenuPosition });
        dispatch({ type: SET_ACTIVE_MENU_PATH, activeMenuPath: [{ id: itemId, text: breadcrumbText }] });
    };

    const renderShortcutButtons = () => {
        return !isMobile &&
            frontendSettings?.enable_shortcut_buttons &&
            menus[Constants.rootMenuId]?.items?.length <= 5 ? (
            <ShortcutButtons
                items={menus[Constants.rootMenuId].items}
                handleShortcutButtonClick={handleShortcutButtonClick}
            />
        ) : null;
    };

    const throttleInterval = 200;
    const registerMenuActivity = useMenuActivityCallback(throttleInterval);

    const renderMenuContent = () => {
        return (
            <div
                ref={el => {
                    if (el) scrollContainerHeightRef.current = el.offsetHeight;
                }}
                style={{ width: "100%", height: "100%" }}
            >
                <CustomScrollbar
                    scrollbarRef={scrollbarRef}
                    onScrollFrame={() => {
                        updateMenuCenterY();
                        registerMenuActivity();
                    }}
                    maxHeight={`calc(100% - ${isMobile ? 0 : 10}px)`}
                >
                    <ContentContainer ref={menuRef}>
                        <SlideIn key={selectedMenu} fromRight={transitionFromRight}>
                            {renderMenuItems()}
                        </SlideIn>
                    </ContentContainer>
                </CustomScrollbar>
            </div>
        );
    };

    const renderFilter = () => {
        let shouldShowFilterButton = showFilters;

        if (filters !== undefined && filters !== null) {
            shouldShowFilterButton = filters.length > 0 && showFilters;
        }

        return (
            <>
                <FilterContainer key="searchAndFilter" theme={shouldShowFilters}>
                    <MenuSearch
                        searchValue={searchValue}
                        isMobile={isMobile}
                        isOpen={searchFocus}
                        onSearchChange={updateSearchValue}
                        onFocus={() => {}}
                    />
                    {shouldShowFilterButton ? (
                        <>
                            <MenuFilter
                                toggleFilters={() => {
                                    setShowFilters(!shouldShowFilters);
                                }}
                                shouldShowFilters={shouldShowFilters}
                                isMobile={isMobile}
                            />

                            {filters ? <MenuChips key="menuChips" shouldShowFilter={shouldShowFilters} /> : <></>}
                        </>
                    ) : (
                        <></>
                    )}
                </FilterContainer>
                <animated.div style={filterContainerSpring}>
                    {shouldShowFilterButton && filters ? (
                        <FilterControls
                            key="filterControls"
                            filters={filters}
                            filterActive={shouldShowFilters}
                            isMobile={isMobile}
                            dynamicType={menuDynamicType}
                        />
                    ) : (
                        <></>
                    )}
                </animated.div>
            </>
        );
    };

    const renderMobileFilter = () => {
        let shouldShowFilterButton = showFilters;

        if (filters !== undefined && filters !== null) {
            shouldShowFilterButton = filters.length > 0 && showFilters;
        }

        return !isMobile || !shouldShowFilterButton ? (
            <></>
        ) : (
            <>
                <animated.div style={filterContainerSpringMobile}>
                    {shouldShowFilterButton && filters ? (
                        <>
                            <FilterControls
                                key="filterControls"
                                filters={filters}
                                filterActive={shouldShowFilters}
                                isMobile={isMobile}
                                dynamicType={menuDynamicType}
                            />
                        </>
                    ) : (
                        <></>
                    )}
                </animated.div>
            </>
        );
    };

    const renderMobilePatternSelector = () => {
        const patterns = menus[selectedMenu]?.availablePatterns;
        return (
            <>
                {patterns?.length > 0 ? (
                    <>
                        <PatternSelector hideLabel={searchFocus} />
                    </>
                ) : null}
            </>
        );
    };

    const renderMobileSearch = () => {
        return (
            <MenuSearch
                searchValue={searchValue}
                isMobile={isMobile}
                isOpen={searchFocus}
                onSearchChange={updateSearchValue}
                onFocus={onSearchFocus}
            />
        );
    };

    const renderExpandMobileContentButton = () => {
        return (
            <>
                <ExpandMobileMenu onClick={() => expandMobileMenu()} expanded={mobileMenuExpanded} />
            </>
        );
    };

    const renderExpandMobileFilterContent = () => {
        let shouldShowFilterButton = showFilters;

        if (filters !== undefined && filters !== null) {
            shouldShowFilterButton = filters.length > 0 && showFilters;
        }

        return !isMobile || shouldShowFilterButton ? (
            <MenuFilter
                toggleFilters={() => {
                    expandFilters();
                }}
                shouldShowFilters={shouldShowFilters}
                isMobile={isMobile}
                hideLabel={isMobile && searchFocus}
            />
        ) : null;
    };

    const renderMenu = () => {
        const filter = renderFilter();
        const content = renderMenuContent();
        const backButton = renderSideMenuBackButton();
        const mobileFilterView = renderMobileFilter();
        const mobilePatternSelector = renderMobilePatternSelector();
        const mobileSearch = renderMobileSearch();
        const expandMobileContent = renderExpandMobileContentButton();
        const expandMobileFilterContent = renderExpandMobileFilterContent();

        let shouldHideFilter = menus[selectedMenu || Constants.rootMenuId]?.items?.every(
            (item: any) => item.type !== ItemType.Item
        );

        shouldHideFilter = shouldHideFilter === undefined ? true : shouldHideFilter;

        if (shouldHideFilter && !isMobile && !favoritesOverlayActive && !layoutOverlayActive) {
            return (
                <SideMenu isDisabled={!menus} side={side} header={<>{backButton}</>}>
                    {content}
                </SideMenu>
            );
        }

        return !isMobile && !favoritesOverlayActive && !layoutOverlayActive ? (
            <SideMenu
                isDisabled={!menus}
                side={side}
                header={
                    <>
                        {backButton}
                        {filter}
                        <FilterFooter
                            numVisibleItems={currentItemLength}
                            hideNumVisibleItems={filteredItems === undefined}
                        />
                        <MenuCategoryTabs />
                    </>
                }
            >
                {content}
            </SideMenu>
        ) : (
            <>
                <animated.div style={mobileContentSpring}>
                    <MobileContentContainer className={mobileMenuExpanded ? "expanded" : ""}>
                        {!shouldHideFilter ? (
                            <div style={{ position: "absolute", width: "100%" }}>{mobileFilterView}</div>
                        ) : (
                            <></>
                        )}

                        {renderFloatMenu()}

                        <div
                            style={{
                                position: "relative",
                                width: "100%",
                                height: "100%",
                                background: "#f7f4f0",
                                display: "flex",
                                flexDirection: "column",
                                zIndex: 6,
                            }}
                        >
                            {loadingMenu ? (
                                <AppLoader color={themeContext.appLoader.color} height={200} width={80} />
                            ) : (
                                <>
                                    {filteredItems !== undefined && !shouldHideFilter ? (
                                        <>
                                            <div
                                                style={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    padding: "10px 0",
                                                    borderBottom: "solid thin #ccc",
                                                    margin: "0 25px",
                                                    justifyContent: "space-between",
                                                    flexDirection: "row-reverse",
                                                }}
                                            >
                                                {expandMobileContent}
                                                {mobilePatternSelector}
                                                {mobileSearch}
                                                {expandMobileFilterContent}
                                                {backButton}
                                            </div>

                                            <VisibleItemsCounter numVisibleItems={currentItemLength} />
                                        </>
                                    ) : (
                                        <>
                                            <div
                                                style={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    padding: "10px 0",
                                                    margin: "0 25px",
                                                    justifyContent: "space-between",
                                                    flexDirection: "row-reverse",
                                                }}
                                            >
                                                {expandMobileContent}
                                                {mobilePatternSelector}
                                                {backButton}
                                            </div>

                                            <VisibleItemsCounter numVisibleItems={currentItemLength} />
                                        </>
                                    )}
                                    <MenuCategoryTabs />
                                    {content}
                                </>
                            )}
                        </div>
                    </MobileContentContainer>
                </animated.div>
            </>
        );
    };

    const renderFullscreenMode = () => {
        return (
            <>
                <ConfiguratorImages fullscreen setShowFilter={() => setShowFilters(false)} />
                <img
                    src={FlipPhoneIcon}
                    style={{ position: "absolute", width: 18, height: 18, top: 0, right: 0, margin: 30 }}
                    alt="Flip phone icon"
                />
            </>
        );
    };

    const renderSaveImageButton = () => {
        return (
            <>
                <SaveImageButton side={side} onClick={() => setShowSaveImageDialog(true)} />
                {showSaveImageDialog ? <SaveImageDialog onClose={() => setShowSaveImageDialog(false)} /> : null}
            </>
        );
    };

    if (isMobile && !breakpoints.portrait) {
        return renderFullscreenMode();
    }

    const currentConfigInFavorite = () => {
        if (!configurationString || configurationString === null) return false;

        const v = configurationString.substring(27, configurationString.length);
        let shouldReturn = false;
        favorites.forEach(favorite => {
            const f = favorite.substring(27, favorite.length);
            if (v === f) {
                shouldReturn = true;
            }
        });

        if (shouldReturn) return true;

        return false;
    };

    const currentFavoritesInConfig = () => {
        return favorites.filter(v => getModelFromConfig(v) === modelId);
    };

    const onLoadFavoriteConfiguration = (removedFavorites: string[], changed: boolean) => {
        // removeConfigurationsFromFavorites(removedFavorites);
        setFavoritesToRemove(removedFavorites);
        if (!changed) dispatch({ type: SET_FAVORITE_OVERLAY_ACTIVE, favoriteOverlayActive: false });
    };

    const onCloseFavorite = (removedFavorites: string[]) => {
        // removeConfigurationsFromFavorites(removedFavorites);
        setFavoritesToRemove(removedFavorites);
        dispatch({ type: SET_FAVORITE_OVERLAY_ACTIVE, favoriteOverlayActive: false });
    };

    const onLoadLayouts = (changed: boolean) => {
        if (!changed) dispatch({ type: SET_LAYOUT_OVERLAY_ACTIVE, layoutOverlayActive: false });
    };

    const renderFloatMenu = () => {
        return (
            <>
                <FloatMenu
                    menuSideRight={side === Side.Right}
                    onLayout={() => {
                        dispatch({ type: SET_LAYOUT_OVERLAY_ACTIVE, layoutOverlayActive: true });
                    }}
                    addFavorite={() => {
                        toggleFavorite(configurationString);
                    }}
                    onFavorite={() => {
                        dispatch({ type: SET_FAVORITE_OVERLAY_ACTIVE, favoriteOverlayActive: true });
                    }}
                    currentConfigInFavorites={currentConfigInFavorite()}
                    hasFavorites={showFavorites && currentFavoritesInConfig().length > 0}
                    hasLayouts={showLayouts && layoutGroups.length > 0}
                    showFavorites={showFavorites}
                />
            </>
        );
    };

    const renderConfigTotalPrice = () => {
        return <>{showPrice && !isMobile ? <ConfiguratorTotalPrice side={side} /> : null}</>;
    };

    return (
        <>
            {showPriceInConfigurator ? renderConfigTotalPrice() : null}
            {showSaveImageButton ? renderSaveImageButton() : null}
            <ConfiguratorImages setShowFilter={() => setShowFilters(false)} />
            {renderMenu()}
            {renderShortcutButtons()}
            <ButtonBack
                onClick={() => {
                    GaReporter.event("back_to_model_selector", "Navigation", undefined, {
                        event_callback: GaReporter.createHitCallback(() => goToModelSelector()),
                    });
                }}
            />

            <ButtonNext menuSideRight={side === Side.Right} onClick={goToSummary} />

            {isMobile ? null : renderFloatMenu()}

            {currentFavoritesInConfig().length > 0 ? (
                <FavoritesOverlay
                    favorites={currentFavoritesInConfig()}
                    onClose={onCloseFavorite}
                    onLoad={onLoadFavoriteConfiguration}
                    isActive={favoritesOverlayActive}
                    currentConfig={configurationString || ""}
                />
            ) : null}

            {layoutGroups.length > 0 && layoutOverlayActive ? (
                <LayoutOverlay
                    layoutGroups={layoutGroups}
                    isActive={layoutOverlayActive}
                    onClose={() => {
                        dispatch({ type: SET_LAYOUT_OVERLAY_ACTIVE, layoutOverlayActive: false });
                    }}
                    onLoad={onLoadLayouts}
                />
            ) : null}
        </>
    );
};

export default Configurator;
